// Crops (HER-8, HER-9, ARCH §28.10). PURE (ENG-1).
//
// A genetic crop's five polygenic genes (`farming.ts` `CROP_GENES`) express into
// `CropTraits` exactly like an animal's `Traits` (`heredity/express.ts`), but they
// are never cached on anything — a crop block entity holds only its `genome`, and
// every site that needs traits calls `expressCrop` fresh (a random tick is far
// rarer than an entity tick, so there is no D-TF-120 case to answer here).
//
// Seed stacking (D-TF-132) compares genomes by `quintileKey`, not by bytes: five
// quintile digits (0..4), one per gene, in table order — the same quintile
// bucketing the tooltip's stars and the stalk-colour tint use. It reads ANY
// genome generically (no gene table needed), which is what lets
// `game/inventory.ts` compare two stacks' components without knowing which crop
// they belong to.
import { geneValue } from './express';
import { decodeGenome, fromBase64 } from './genome';
import type { Gene } from './genes';
import type { Rng } from '../engine/rng';

/** A raw byte's quintile bucket, 0..4 (§28.3's `stalkQ` formula, reused for every
 *  gene's star rating): `min(4, floor(v / 51.2))`. */
export function quintileOf(v: number): number {
  return Math.min(4, Math.floor(v / 51.2));
}

/** The stacking signature (D-TF-132): one quintile digit per byte, in genome
 *  order. No genome (undefined/empty/invalid) ⇒ `''`, so two genome-less seeds
 *  share a signature and stack, and a genome-less seed never matches a genetic
 *  one's. */
export function quintileKey(genome: string | undefined): string {
  if (!genome) return '';
  const bytes = fromBase64(genome);
  if (!bytes) return '';
  return Array.from(bytes, quintileOf).join('');
}

/** One quintile as filled/empty stars, 5 wide (§28.10's tooltip). */
export function stars(q: number): string {
  const filled = q + 1;
  return '★'.repeat(filled) + '☆'.repeat(5 - filled);
}

/** The tooltip's second line (binding, `plans/HEREDITY.md` §4.3): yield, growth,
 *  drought tolerance ("Hardy"), each as its own quintile's stars. */
export function starsLine(qYield: number, qGrowth: number, qDrought: number): string {
  return `Yield ${stars(qYield)} · Growth ${stars(qGrowth)} · Hardy ${stars(qDrought)}`;
}

/** The scalars a crop genome expresses (§28.10). Computed fresh from the block
 *  entity's genome at every site that needs it — never cached (there is no
 *  per-tick cost to cache away; a crop is read at most once per random tick). */
export interface CropTraits {
  readonly growthMul: number;
  readonly yieldMul: number;
  readonly seedBonus: number;
  readonly droughtTol: number;
  /** 0..4 — indexes `HER.STALK_HEX`. */
  readonly stalkQ: number;
}

export function expressCrop(genes: readonly Gene[], genome: Uint8Array): CropTraits {
  let growthMul = 1, yieldMul = 1, seedBonus = 0, droughtTol = 0, stalkQ = 0;
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') { at += 2; continue; }
    const v = genome[at++]!;
    switch (g.expresses.scalar) {
      case 'growthMul': growthMul = geneValue(v, g.expresses.range); break;
      case 'yieldMul': yieldMul = geneValue(v, g.expresses.range); break;
      case 'seedBonus': seedBonus = Math.round(geneValue(v, g.expresses.range)); break;
      case 'droughtTol': droughtTol = geneValue(v, g.expresses.range); break;
      // §28.3: stalkQ reads the RAW byte's quintile, not the mapped range value
      // (the declared `[0,1]` range only marks the gene's shape).
      case 'stalkQ': stalkQ = quintileOf(v); break;
      default: break;
    }
  }
  return Object.freeze({ growthMul, yieldMul, seedBonus, droughtTol, stalkQ });
}

/** A random-tick growth check scaled by two genes (§28.10): drought tolerance
 *  raises dry farmland toward the hydrated moisture level before the chance is
 *  read, and `growthMul` scales the resulting chance directly. `chance` is
 *  `farming.ts`'s `growthChance` — passed in rather than imported, so this stays
 *  the one place the formula's two inputs meet. */
export function growthRoll(moisture: number, droughtTol: number, growthMul: number, chance: (m: number) => number, roll: number): boolean {
  const m = moisture >= 7 ? 7 : Math.max(moisture, Math.round(7 * droughtTol));
  return roll < chance(m) * growthMul;
}

/** Produce count at harvest (§28.10): `floor(n · yieldMul)` plus one more with
 *  probability equal to the fractional remainder — so the LONG-RUN mean is
 *  exactly `n · yieldMul` while every single harvest is an integer. */
export function produceCount(base: number, yieldMul: number, roll: number): number {
  const exact = base * yieldMul;
  const whole = Math.floor(exact);
  return whole + (exact - whole > roll ? 1 : 0);
}

/** The tooltip's second line (§28.10) for a held/hovered seed stack, or null when
 *  it should not show: the rule is off, the stack carries no genome, its crop has
 *  no gene table, or the genome fails to decode against it (E-51 — no line rather
 *  than a wrong one). PURE and DOM-free so it is unit-testable without a window;
 *  `client/ui/screens/container.ts` calls it straight into the tooltip element. */
export function cropTooltipLine(genes: readonly Gene[] | undefined, genome: string | undefined, heredityOn: boolean): string | null {
  if (!heredityOn || !genome || !genes) return null;
  const bytes = decodeGenome(genome, genes);
  if (!bytes) return null;
  let qYield = 0, qGrowth = 0, qDrought = 0;
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') { at += 2; continue; }
    const v = bytes[at++]!;
    if (g.expresses.scalar === 'yieldMul') qYield = quintileOf(v);
    else if (g.expresses.scalar === 'growthMul') qGrowth = quintileOf(v);
    else if (g.expresses.scalar === 'droughtTol') qDrought = quintileOf(v);
  }
  return starsLine(qYield, qGrowth, qDrought);
}

/** Cross-pollination's mate pick (§28.10/§3.7): one of the mature same-crop
 *  neighbour genomes the caller already gathered, chosen by `rng`; none ⇒ null
 *  (the caller selfs — `recombine(genes, self, self, rng)`, which is exactly
 *  correct: the plant's own two alleles still recombine, and a polygenic gene's
 *  "parents' mean" of one parent with itself is itself, plus noise). */
export function pollenMate(neighbors: readonly Uint8Array[], rng: Rng): Uint8Array | null {
  return neighbors.length === 0 ? null : neighbors[rng.nextInt(neighbors.length)]!;
}
